import {
	CheckCircle2,
	Cpu,
	Image,
	MessageSquare,
	Music,
	Plus,
	RefreshCw,
	Server,
	Trash2,
	Video,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { giveNsfwConsent } from "../../services/auth";
import {
	listMyRouteConfigs,
	type RouteConfig,
} from "../../services/route-configs";
import { NsfwConsentModal } from "../auth/NsfwConsentModal";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type GenerationTab = "text" | "image" | "audio" | "video";

export interface PlaygroundSelection {
	routeConfig: RouteConfig | null;
	generationType: GenerationTab;
}

interface PlaygroundTopBarProps {
	selection: PlaygroundSelection;
	onSelectionChange: (selection: PlaygroundSelection) => void;
	onCreateConfig: () => void;
	onClearConversation?: () => void;
	hasMessages?: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function PlaygroundTopBar({
	selection,
	onSelectionChange,
	onCreateConfig,
	onClearConversation,
	hasMessages = false,
}: PlaygroundTopBarProps) {
	const [routeConfigs, setRouteConfigs] = useState<RouteConfig[]>([]);
	const [loadingConfigs, setLoadingConfigs] = useState(true);
	const [consentModalOpen, setConsentModalOpen] = useState(false);
	const [pendingRouteConfig, setPendingRouteConfig] =
		useState<RouteConfig | null>(null);
	const { user, checkAuth } = useAuth();

	const loadConfigs = useCallback(
		async (generationType: GenerationTab) => {
			setLoadingConfigs(true);
			const res = await listMyRouteConfigs({
				page: 1,
				limit: 100,
				generationType,
			});
			setRouteConfigs(res.data || []);
			// For route configs, we check if they have connected model configs
			const connected = (res.data || []).filter((c) => {
				// Check if route config has at least one connected model config
				return c.models?.some(
					(m) => m.modelConfig.connectionStatus === "connected",
				);
			});
			// Auto-select first connected if none selected
			// But check NSFW consent first
			if (connected.length > 0 && !selection.routeConfig) {
				const firstConnected = connected[0];
				// Check if it's NSFW and user hasn't consented
				if (firstConnected.isNsfw && !user?.nsfwConsentGiven) {
					console.log(
						"[PlaygroundTopBar] Auto-selected route config is NSFW, showing consent modal",
					);
					setPendingRouteConfig(firstConnected);
					setConsentModalOpen(true);
				} else {
					onSelectionChange({ ...selection, routeConfig: firstConnected });
				}
			}
			setLoadingConfigs(false);
		},
		[onSelectionChange, selection, user?.nsfwConsentGiven],
	);

	useEffect(() => {
		loadConfigs(selection.generationType);
	}, [selection.generationType, loadConfigs]);

	const handleRouteConfigChange = (configId: string) => {
		if (configId === "__create__") {
			onCreateConfig();
			return;
		}
		const routeConfig = routeConfigs.find((c) => c.id === configId) || null;

		console.log("[PlaygroundTopBar] Route config change:", {
			configId,
			routeConfig: routeConfig
				? {
						id: routeConfig.id,
						name: routeConfig.name,
						isNsfw: routeConfig.isNsfw,
					}
				: null,
			userConsentGiven: user?.nsfwConsentGiven,
			userId: user?.id,
		});

		// Check if route config is NSFW and user hasn't consented
		if (routeConfig?.isNsfw && !user?.nsfwConsentGiven) {
			console.log(
				"[PlaygroundTopBar] NSFW route config detected, showing consent modal",
			);
			setPendingRouteConfig(routeConfig);
			setConsentModalOpen(true);
			return;
		}

		onSelectionChange({ ...selection, routeConfig });
	};

	const handleConsent = async () => {
		try {
			await giveNsfwConsent();
			await checkAuth(); // Refresh user data
			toast.success("Consent recorded. You can now access NSFW route configs.");

			// Apply the pending selection after consent
			if (pendingRouteConfig) {
				onSelectionChange({ ...selection, routeConfig: pendingRouteConfig });
				setPendingRouteConfig(null);
			}
		} catch (error: any) {
			toast.error(error.message || "Failed to record consent");
			throw error;
		}
	};

	const handleTabChange = (tab: string) => {
		onSelectionChange({
			...selection,
			generationType: tab as GenerationTab,
			routeConfig: null,
		});
	};

	const getConnectionBadge = (status: string) => {
		const colors: Record<string, string> = {
			connected: "bg-green-500/20 text-green-600 border-green-500/30",
			pending: "bg-yellow-500/20 text-yellow-600 border-yellow-500/30",
			error: "bg-red-500/20 text-red-600 border-red-500/30",
		};
		return colors[status] || colors.pending;
	};

	return (
		<div className="border border-border bg-card rounded-xl">
			{/* Selectors Row */}
			<div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-6 p-4 md:p-5">
				{/* Model Config Selector */}
				<div className="flex flex-col gap-2">
					<div className="flex items-center gap-1">
						<label className="text-xs text-muted-foreground font-medium">
							Model Config
						</label>
						<div className="flex items-center gap-0 ml-2">
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8"
								onClick={() => loadConfigs(selection.generationType)}
								aria-label="Refresh model configs"
							>
								<RefreshCw className="h-4 w-4" />
							</Button>
							{onClearConversation && (
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
									onClick={onClearConversation}
									disabled={!hasMessages}
									aria-label="Clear conversation"
									title="Clear conversation"
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							)}
						</div>
					</div>
					{loadingConfigs ? (
						<Skeleton className="h-10 w-full md:w-80" />
					) : (
						(() => {
							const connectedConfigs = routeConfigs.filter((c) => {
								// Check if route config has at least one connected model config
								return c.models?.some(
									(m) => m.modelConfig.connectionStatus === "connected",
								);
							});
							if (connectedConfigs.length === 0) {
								return (
									<div className="p-3 border border-dashed rounded-md text-xs text-muted-foreground w-full md:w-80">
										No connected route configs.
										<Button
											variant="link"
											className="px-0 text-primary text-xs"
											onClick={onCreateConfig}
										>
											Create one
										</Button>
									</div>
								);
							}
							return (
								<Select
									value={selection.routeConfig?.id || ""}
									onValueChange={handleRouteConfigChange}
								>
									<SelectTrigger className="w-full md:w-80 md:min-w-80 h-10">
										<SelectValue placeholder="Select route config" />
									</SelectTrigger>
									<SelectContent className="w-[var(--radix-select-trigger-width)] md:min-w-80">
										{connectedConfigs.map((config) => {
											const primaryModelConfig = config.models?.find(
												(m) => m.isPrimary,
											)?.modelConfig;
											const connectionStatus =
												primaryModelConfig?.connectionStatus || "unknown";
											return (
												<SelectItem key={config.id} value={config.id}>
													<div className="flex items-center gap-2">
														<span>{config.name}</span>
														<Badge
															variant="outline"
															className={`text-[10px] px-1 py-0 ${getConnectionBadge(connectionStatus)}`}
														>
															{connectionStatus}
														</Badge>
													</div>
												</SelectItem>
											);
										})}
										<SelectItem value="__create__" className="text-primary">
											<div className="flex items-center gap-2">
												<Plus className="h-3 w-3" />
												<span>Create New Route Config</span>
											</div>
										</SelectItem>
									</SelectContent>
								</Select>
							);
						})()
					)}
				</div>

				{/* Selected Route Config Info */}
				{selection.routeConfig &&
					(() => {
						const primaryModelConfig = selection.routeConfig.models?.find(
							(m) => m.isPrimary,
						)?.modelConfig;
						const connectionStatus =
							primaryModelConfig?.connectionStatus || "unknown";
						return (
							<div className="flex-1 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 px-4 md:px-6 py-4 bg-muted/30 border border-border/50 rounded-lg">
								{/* Route Config Name & Status */}
								<div className="flex-1 flex flex-col gap-2 w-full md:w-auto">
									<div className="flex items-center gap-2 flex-wrap">
										<h3 className="text-sm font-semibold text-foreground">
											{selection.routeConfig.name}
										</h3>
										<Badge
											variant="outline"
											className={`text-[10px] px-2 py-0.5 flex items-center gap-1 ${getConnectionBadge(connectionStatus)}`}
										>
											{connectionStatus === "connected" && (
												<CheckCircle2 className="h-3 w-3" />
											)}
											{connectionStatus}
										</Badge>
									</div>
									{primaryModelConfig && (
										<div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 text-xs text-muted-foreground">
											<div className="flex items-center gap-1.5">
												<Server className="h-3.5 w-3.5" />
												<span className="font-medium">Provider:</span>
												<Badge
													variant="outline"
													className="capitalize text-xs px-2 py-0.5 font-normal"
												>
													{primaryModelConfig.providerType}
												</Badge>
											</div>
											<div className="flex items-center gap-1.5">
												<Cpu className="h-3.5 w-3.5" />
												<span className="font-medium">Model:</span>
												<span className="font-mono font-semibold text-foreground">
													{primaryModelConfig.modelDisplayName ||
														primaryModelConfig.modelIdentifier}
												</span>
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})()}
			</div>

			{/* Generation Type Tabs */}
			<div className="px-5 pb-4 border-t border-border/50">
				<Tabs value={selection.generationType} onValueChange={handleTabChange}>
					<TabsList className="gap-1">
						<TabsTrigger value="text" className="gap-2">
							<MessageSquare className="h-4 w-4" />
							Text Chat
						</TabsTrigger>
						<TabsTrigger value="image" className="gap-2" disabled>
							<Image className="h-4 w-4" />
							Image
							<Badge
								variant="secondary"
								className="text-[10px] px-1.5 py-0 ml-1"
							>
								Soon
							</Badge>
						</TabsTrigger>
						<TabsTrigger value="audio" className="gap-2" disabled>
							<Music className="h-4 w-4" />
							Audio
							<Badge
								variant="secondary"
								className="text-[10px] px-1.5 py-0 ml-1"
							>
								Soon
							</Badge>
						</TabsTrigger>
						<TabsTrigger value="video" className="gap-2" disabled>
							<Video className="h-4 w-4" />
							Video
							<Badge
								variant="secondary"
								className="text-[10px] px-1.5 py-0 ml-1"
							>
								Soon
							</Badge>
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			<NsfwConsentModal
				open={consentModalOpen}
				onOpenChange={setConsentModalOpen}
				onConsent={handleConsent}
			/>
		</div>
	);
}
